import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getAccount } from './api/auth';
import { isAuthenticated } from './storage';
import en from './locales/en.json';

export const DEFAULT_LANGUAGE = 'en';

const catalogs = { en };

const I18nContext = createContext({
    language: DEFAULT_LANGUAGE,
    t: (key) => key,
});

function resolve(catalog, key) {
    let node = catalog;
    for (const part of key.split('.')) {
        if (!node || typeof node !== 'object' || !(part in node)) {
            return null;
        }
        node = node[part];
    }
    return typeof node === 'string' ? node : null;
}

function translate(key, language, params = {}) {
    const lang = catalogs[language] ? language : DEFAULT_LANGUAGE;
    let template = resolve(catalogs[lang], key);
    if (template == null && lang !== DEFAULT_LANGUAGE) {
        template = resolve(catalogs[DEFAULT_LANGUAGE], key);
    }
    if (template == null) {
        return key;
    }
    return template.replace(/\{(\w+)\}/g, (match, name) => (
        Object.prototype.hasOwnProperty.call(params, name) ? params[name] : match
    ));
}

export function I18nProvider({ children }) {
    const [language, setLanguage] = useState(DEFAULT_LANGUAGE);

    useEffect(() => {
        if (!isAuthenticated()) {
            setLanguage(DEFAULT_LANGUAGE);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const data = await getAccount();
                const userLang = data.user?.default_language;
                if (!cancelled && userLang && catalogs[userLang]) {
                    setLanguage(userLang);
                }
            } catch (err) {
                // Keep the default language when the profile cannot be loaded.
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const t = useCallback(
        (key, params) => translate(key, language, params),
        [language],
    );

    return (
        <I18nContext.Provider value={{ language, t, setLanguage }}>
            {children}
        </I18nContext.Provider>
    );
}

export function useTranslation() {
    return useContext(I18nContext);
}
